import { axiosGet, axiosPost } from '../api/axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

export const featureFlagKeys = {
  featureFlags: ['featureFlags'] as const,
};
export interface FeatureFlags {
  count: number;
  data: { [key: string]: FeatureFlag };
}

export interface FeatureFlag {
  created_at: Date;
  description: string;
  enabled: boolean;
  id: number;
  key: string;
  client_ids?: number[];
}

const getFeatureFlags = async (): Promise<FeatureFlags> => {
  return await axiosGet(`/feature_flags`, {}, 'v3').then((response) => response.data);
};

export enum SwitchboardFeatures {}

export const useFeatureFlag = (key: keyof typeof SwitchboardFeatures) => {
  const queryClient = useQueryClient();
  const query = useQuery<FeatureFlags>(featureFlagKeys.featureFlags, () => getFeatureFlags(), {
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
  });
  return {
    ...query,
    enabled: !!query.data?.data[key]?.enabled,
    setEnabled: (newValue: Boolean) => {
      queryClient.setQueryData(['featureFlags'], {
        ...query.data,
        data: {
          ...query.data?.data,
          [key]: {
            ...query.data?.data?.[key],
            enabled: newValue,
          },
        },
      });
    },
  };
};

export const useFeatureFlagsList = () => {
  return useQuery<FeatureFlags>(featureFlagKeys.featureFlags, () => getFeatureFlags(), {
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
  });
};

const toggleFeatureFlagRequest = async (key: string): Promise<FeatureFlag> => {
  return await axiosPost(`/feature_flags/${key}/toggle`, {}, 'v3').then((response) => response.data);
};

export const useToggleFeatureFlag = () => {
  const queryClient = useQueryClient();
  return useMutation((key: string) => toggleFeatureFlagRequest(key), {
    onSuccess: (updated) => {
      queryClient.setQueryData<FeatureFlags>(featureFlagKeys.featureFlags, (old) => {
        if (!old) return old as any;
        return {
          ...old,
          data: {
            ...old.data,
            [updated.key]: updated,
          },
          count: Object.keys(old.data || {}).length,
        } as FeatureFlags;
      });
      toast.success(`Toggled ${updated.key}`);
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.description || 'Failed to toggle feature flag';
      toast.error(msg);
    },
  });
};

const toggleFeatureFlagForClientRequest = async (key: string, clientId: number): Promise<FeatureFlag> => {
  return await axiosPost(`/feature_flags/${key}/toggle/${clientId}`, {}, 'v3').then((response) => response.data);
};

export const useToggleFeatureFlagForClient = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ key, clientId }: { key: string; clientId: number }) => toggleFeatureFlagForClientRequest(key, clientId),
    {
      onSuccess: (updated) => {
        queryClient.setQueryData<FeatureFlags>(featureFlagKeys.featureFlags, (old) => {
          if (!old) return old as any;
          return {
            ...old,
            data: {
              ...old.data,
              [updated.key]: updated,
            },
            count: Object.keys(old.data || {}).length,
          } as FeatureFlags;
        });
        toast.success(`Toggled client access for ${updated.key}`);
      },
      onError: (error: any) => {
        const msg = error?.response?.data?.description || 'Failed to toggle feature flag for client';
        toast.error(msg);
      },
    },
  );
};
